import { useState, type FormEvent } from "react";
import "./recovery.css";

interface PasswordRecoveryPageProps {
  completed: boolean;
  loading: boolean;
  ready: boolean;
  onContinue(): void;
  onUpdate(password: string): Promise<string | null>;
}

export function PasswordRecoveryPage({
  completed,
  loading,
  ready,
  onContinue,
  onUpdate,
}: PasswordRecoveryPageProps) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (password !== confirmation) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setSubmitting(true);
    const message = await onUpdate(password);
    setSubmitting(false);
    if (message) setError(message);
  }

  if (completed) {
    return (
      <main className="auth-page recovery-shell">
        <section className="auth-card" aria-live="polite">
          <p className="eyebrow">Recuperación completada</p>
          <h2>Contraseña actualizada</h2>
          <p>Ya puedes ingresar al portal utilizando tu contraseña nueva.</p>
          <button onClick={onContinue} type="button">
            Volver al inicio de sesión
          </button>
        </section>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="auth-page recovery-shell">
        <section className="auth-card" aria-live="polite">
          <p>Validando el enlace de recuperación…</p>
        </section>
      </main>
    );
  }

  if (!ready) {
    return (
      <main className="auth-page recovery-shell">
        <section className="auth-card">
          <p className="eyebrow">Enlace no válido</p>
          <h2>Solicita un enlace nuevo</h2>
          <p>El enlace de recuperación venció, ya fue utilizado o no pudo validarse.</p>
          <button onClick={onContinue} type="button">
            Volver al inicio de sesión
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page recovery-shell">
      <form className="auth-card" onSubmit={submit}>
        <p className="eyebrow">Recuperación de acceso</p>
        <h2>Establece una contraseña nueva</h2>
        <p>Utiliza al menos 8 caracteres y evita reutilizar una contraseña anterior.</p>
        <label>
          Contraseña nueva
          <input
            autoComplete="new-password"
            minLength={8}
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </label>
        <label>
          Confirmar contraseña
          <input
            autoComplete="new-password"
            minLength={8}
            onChange={(event) => setConfirmation(event.target.value)}
            required
            type="password"
            value={confirmation}
          />
        </label>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button disabled={submitting} type="submit">
          {submitting ? "Actualizando…" : "Guardar contraseña nueva"}
        </button>
      </form>
    </main>
  );
}
